import SectionWrapper from '../SectionWrapper';
import {
  FormControl,
  Input,
  FormErrorMessage,
  FormLabel,
  Flex,
  Box,
  Tooltip,
} from '@chakra-ui/react';
import SwitchField from '../SwitchField';
import { useAppSelector } from '../../../../store/redux/hooks';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import {
  prefillFormBranding,
  setFormField,
} from '../../../../store/redux/slices/form.slice';
import { brandingFromTemplate } from './formTheme';
import {
  hasDefaultBranding,
  templateIcon,
  templateLogo,
} from './templateBranding';
import resizeToFavicon from '../utils/resizeToFavicon';
import { useAppDispatch } from '../../../../store/redux/hooks';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import { FormState } from '../../../../store/types/form';
import FieldEditIndicator from '../FieldEditIndicator';
import { useRef } from 'react';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import { useValidateSlug } from '../hooks/useValidateSlug';

export const PUBLISH_TO_ENABLE_FORM_MESSAGE =
  'Please publish your template first to create an issuance form.';

const FormDetails = ({ enableLocked = false }: { enableLocked?: boolean }) => {
  const formEnabled = useAppSelector((s) => s.form.enabled);
  const slug = useAppSelector((s) => s.form.slug);
  const templateId = useAppSelector((s) => s.template.uuid);
  const errors = useAppSelector((s) => s.templateErrors);
  const dispatch = useAppDispatch();
  const { handleFocus, validateField, validateSection } =
    useTemplateFieldValidation('form');
  const form = useAppSelector((s) => s.form);
  const templateColors = useAppSelector((s) => s.template.colors);
  const templateImages = useAppSelector((s) => s.template.images);
  const { validateSlug } = useValidateSlug(templateId);
  const formEnabledRef = useRef<HTMLDivElement>(null);
  const slugRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      formEnabled: formEnabledRef,
      slug: slugRef,
    },
  });

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    if (enableLocked && field === 'enabled') return;
    dispatch(setFormField({ field, value }));
    let updatedForm = { ...form, [field]: value };

    if (field === 'enabled' && value === false) {
      dispatch(setFormField({ field: 'slugTouched', value: false }));
      updatedForm = { ...updatedForm, slugTouched: false };
    }

    validateField(field, value, { form: updatedForm, validateSlug });

    if (field === 'enabled') {
      validateSection(updatedForm, { validateSlug });
    }

    if (field === 'enabled' && value === true && hasDefaultBranding(form)) {
      dispatch(
        prefillFormBranding({
          colors: brandingFromTemplate(templateColors),
          logo: templateLogo(templateImages),
        })
      );

      const icon = templateIcon(templateImages);
      if (icon) {
        resizeToFavicon(icon)
          .catch(() => icon)
          .then((favicon) =>
            dispatch(
              setFormField({ field: 'formFaviconImage', value: favicon })
            )
          );
      }
    }
  };

  const markSlugTouched = () => {
    dispatch(setFormField({ field: 'slugTouched', value: true }));
  };

  return (
    <SectionWrapper
      title="Form details"
      openOnLoad={true}
      id="form-details-section"
    >
      <Flex direction="column" gap={4}>
        <FieldEditIndicator
          fieldRef={formEnabledRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="50px"
        >
          <FormControl onClick={() => handleFocus('formEnabled')} mb={1}>
            <Flex align="center" justify="space-between" w="full">
              <FormLabel variant="templateBuilderLabel" mb={0}>
                Enable pass issuance form
              </FormLabel>
              <Tooltip
                label={PUBLISH_TO_ENABLE_FORM_MESSAGE}
                isDisabled={!enableLocked}
                hasArrow
                placement="top"
                fontSize="12px"
                bg="inkBlue"
                color="white"
                borderRadius="6px"
                textAlign="center"
                p="10px"
              >
                <Box display="inline-flex">
                  <SwitchField
                    value={formEnabled}
                    disabled={enableLocked}
                    onChange={(value) => {
                      handleChange('enabled', value);
                    }}
                  />
                </Box>
              </Tooltip>
            </Flex>
          </FormControl>
        </FieldEditIndicator>
        <FieldEditIndicator
          fieldRef={slugRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="54px"
        >
          <FormControl
            isInvalid={!!errors['form.slug']}
            isRequired={formEnabled}
          >
            <FormLabel variant="templateBuilderLabel">Form name</FormLabel>
            <Input
              value={slug}
              onChange={(e) => {
                markSlugTouched();
                handleChange(
                  'slug',
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                );
              }}
              placeholder="Enter a unique form name"
              onFocus={() => {
                handleFocus('slug');
                markSlugTouched();
              }}
              isDisabled={!formEnabled}
            />
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors['form.slug']}
            </FormErrorMessage>
          </FormControl>
        </FieldEditIndicator>
      </Flex>
    </SectionWrapper>
  );
};

export default FormDetails;
